import { JSX } from 'react';
import DOMPurify from 'dompurify';
import { hostUrl, frontUrl } from "../utils/url-manager";
import { TemplateEditor } from "./components/TemplateEditor";
import { TemplateVariable, SamplePerson, samplePerson } from "./components/template-variables";

export const EMAIL_VARIABLES: TemplateVariable[] = [
    { token: '{{name}}',                     description: "The recipient's name." },
    { token: '{{user_email}}',               description: "The recipient's email address, as a mailto link." },
    { token: '{{reset_url}}',                description: 'A password reset link. Expires one hour after the email is sent.' },
    { token: '{{verify_url}}',               description: "A link to verify the recipient's email address." },
    { token: '{{login_url}}',                description: 'The customer login page.' },
    { token: '{{support_email}}',            description: 'The NokNok support address.' },
    { token: '{{current_year}}',             description: 'The current year, for copyright lines.' },
    { token: '{{invoice_login_url}}',        description: 'The account page where customers view their invoices.' },
    { token: '{{invoice_id}}',               description: 'The invoice reference.' },
    { token: '{{customer_billing_address}}', description: "The customer's billing address, as an <address> block." },
    { token: '{{subscription_table_items}}', description: 'A table of the subscription items, or the single purchase.' },
    // HQ-05. These three are in the stored invite template and are substituted by
    // `CustomerUsersHandler::processEmailHTML()`, but were missing from this list, so the
    // preview rendered them as literal `{{...}}` and nothing told an admin they existed.
    // They only have a value on the account invitation email; every other email leaves them
    // in place, which is the same as it has always been.
    { token: '{{invite_recipient}}',         description: 'Account invitations only. The name of the person being invited.' },
    { token: '{{inviter_name}}',             description: 'Account invitations only. The name of the customer sending the invitation.' },
    { token: '{{invitation_url}}',           description: 'Account invitations only. The one-time link that accepts the invitation. It carries an encrypted payload and expires.' },
];

/** Sample values for the preview. Rebuilt per preview so one person is used throughout. */
export function buildSamples(): Record<string, string> {
    const person: SamplePerson = samplePerson(hostUrl);
    const account: string = frontUrl + 'account/';

    return {
        '{{name}}': `<span>${person.name}</span>`,
        '{{user_email}}': `<a href="mailto:${person.email}" `
            + `style="color:inherit;text-decoration:inherit;font-size:inherit;font-weight:inherit">`
            + `${person.email}</a>`,
        // Previously previewed as google.com, which misrepresented the link.
        '{{reset_url}}': account + 'reset-password/?token=sample',
        '{{verify_url}}': account,
        '{{login_url}}': account,
        '{{invoice_login_url}}': account,
        '{{support_email}}': 'support@noknok.ai',
        '{{current_year}}': new Date().getFullYear().toString(),
        '{{invoice_id}}': 's3hfgIduFhgu',
        '{{invite_recipient}}': 'Sam Newman',
        '{{inviter_name}}': 'Joe Bloggs',
        '{{invitation_url}}': frontUrl + 'account/invitation/sample',
        '{{customer_billing_address}}': `
                <address style="width:100%;text-align:right;">
                    Joe Bloggs</br>
                    123 Street </br>
                    In A City </br>
                    Some County </br>
                    AA1 1AA</br>
                </address>
            `,
        '{{subscription_table_items}}': `
                <table role="presentation" cellpadding="0" cellspacing="0" style="margin:18px 0 8px 0;color:white;width:100%">
                    <thead style="border-bottom:1px solid #ccc;margin: 0 0 10px 0;">
                        <tr>
                            <th>Subscription Tier</th>
                            <th>Price</th>
                            <th>Seats</th>
                        </tr>
                   </thead>
                   <tbody>
                        <tr style="margin:10px 0 0 0;">
                            <td style="text-align:center;" >BusinessPlus</td>
                            <td style="text-align:center;" >£100.00</td>
                            <td style="text-align:center;" >5</td>
                        </tr>
                   </tbody>
               </table>
            `,
    };
}

/** A fragment keeps its document tags stripped; a full document keeps them. */
function sanitizeEmailHtml(value: string): string {
    const isFullDocument: boolean = /<html[\s>]/i.test(value);
    return DOMPurify.sanitize(value, {
        ...(!isFullDocument && { FORBID_TAGS: ['html', 'head', 'body'] }),
    });
}

export const SystemEmailTemplateCodeEditor: () => JSX.Element = (): JSX.Element => (
    <TemplateEditor
        intro={ 'Inline-styled HTML for this system email. Script tags are removed as you type. '
            + 'Remember that Outlook and several other clients ignore most modern CSS.' }
        variables={ EMAIL_VARIABLES }
        buildSamples={ buildSamples }
        sanitize={ sanitizeEmailHtml }
    />
);
